import {
  ClickAwayListener,
  makeStyles,
  ListItem,
  ListItemText,
  Typography,
  useInfiniteScroll,
  useBreakpoint
} from '@paintscout/ui'
import type { MuiTheme as Theme, ListItemProps, StyleClasses } from '@paintscout/ui'
import type { Contact, User } from '@paintscout/api'
import React, { useRef } from 'react'
import moment from 'moment'
import { getOwnerDisplayName } from '@paintscout/util/builder'
import { useUsers } from '@paintscout/ui-business'

const getContactSourceDisplayName = (source: string): string => {
  switch (source) {
    case 'pipeline_deals':
      return 'Pipeline CRM'
    case 'service_titan':
      return 'Service Titan'
    case 'salesforce':
      return 'Salesforce'
    default:
      return ''
  }
}

const useStyles = makeStyles<Theme, ContactResultsListProps>(
  (theme) => ({
    root: {
      height: '100%',
      maxHeight: '60vh',
      overflow: 'auto',
      [theme.breakpoints.down('sm')]: {
        maxHeight: '30vh'
      },
      [theme.breakpoints.down('xs')]: {
        maxHeight: '60vh'
      }
    },
    row: {}
  }),
  { name: 'ContactResultsList' }
)

export interface ContactResultsListProps {
  classes?: StyleClasses<typeof useStyles>
  disabled?: boolean
  loading?: boolean
  hasMore?: boolean
  results: Array<Partial<Contact> & { dealId?: number }>
  onLoadMore: () => any
  onContactClick: (contact: Partial<Contact & { dealId?: number }>) => any
  onClickAway?: (event: React.MouseEvent<Document, MouseEvent>) => void
}

function ContactResultsList(props: ContactResultsListProps) {
  const classes = useStyles(props)
  const { disabled, loading, hasMore, results, onContactClick, onLoadMore, onClickAway } = props
  const ref = useRef(null)
  // Read once for the whole list rather than per row — each item resolves its owner name against it.
  const { usersById } = useUsers()

  useInfiniteScroll({
    ref,
    onLoadMore,
    scrollPercentage: 95,
    disabled: disabled || loading || hasMore === false
  })

  return (
    <ClickAwayListener
      onClickAway={(event) => {
        if (onClickAway) onClickAway(event)
      }}
    >
      <div className={classes.root} ref={ref}>
        {results.map((contact, index) => (
          <ContactListItem
            className={`contact-list-${index}`}
            key={index}
            disabled={disabled}
            contact={contact}
            usersById={usersById}
            onClick={() => onContactClick(contact)}
          />
        ))}
      </div>
    </ClickAwayListener>
  )
}

/*******************************
 *         ContactListItem     *
 *******************************/
const useListItemStyles = makeStyles((theme: Theme) => ({
  listItem: {
    maxWidth: '100%',
    overflowWrap: 'normal',
    display: 'flex',
    justifyContent: 'space-between',
    // on mobile, stack the name/address column above the email/source column
    // so nothing is squeezed against (or off) the right edge of the dialog
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'stretch'
    }
  },
  listItemText: {
    flex: 1,
    paddingRight: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      paddingRight: 0
    }
  },
  details: {
    [theme.breakpoints.down('sm')]: {
      marginTop: theme.spacing(0.5),
      textAlign: 'left'
    }
  },
  secondaryText: {
    color: theme.paintscout.palette.brand.Neutral.P70,
    fontStyle: 'italic',
    overflowWrap: 'normal',
    [theme.breakpoints.down('sm')]: {
      maxWidth: '75%'
    }
  },
  sourceName: {
    color: theme.paintscout.palette.brand.Neutral.P70,
    fontStyle: 'italic',
    paddingRight: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      // stacked layout: fill the row, left-aligned (overrides Typography align="right")
      maxWidth: '100%',
      paddingRight: 0,
      textAlign: 'left'
    }
  },
  email: {
    fontStyle: 'italic',
    overflowWrap: 'break-word',
    wordBreak: 'break-all',
    maxWidth: 280,
    paddingRight: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      maxWidth: '100%',
      paddingRight: 0,
      textAlign: 'left'
    }
  },
  owner: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: 280,
    paddingRight: theme.spacing(2),
    fontWeight: 600,
    [theme.breakpoints.down('sm')]: {
      maxWidth: '100%',
      paddingRight: 0,
      textAlign: 'left'
    }
  }
}))

function ContactListItem({
  contact,
  usersById,
  ...props
}: {
  contact: Partial<Contact>
  usersById?: Record<string, User>
} & ListItemProps) {
  const classes = useListItemStyles({})
  const isSmallScreen = useBreakpoint((breakpoints) => breakpoints.down('sm'))
  // Live name, falling back to the contact's owner snapshot — see getOwnerDisplayName (#21800).
  const ownerName = getOwnerDisplayName({ owner: contact.owner, users: usersById })

  const primaryText = (
    <>
      <strong>
        {contact.first_name} {contact.last_name}
      </strong>
      {!!contact.company && ` - ${contact.company}`}
      {!!contact.stAppointmentDate && ` - ${moment(contact.stAppointmentDate)}`.split(' GMT')[0]}
    </>
  )

  const secondaryText = [contact.address, contact.city, contact.province].filter((item) => item).join(', ')
  const textBodySize = isSmallScreen ? 'body2' : 'body1'
  let contactSource = getContactSourceDisplayName(contact.source)
  let contactSourceSecondary = ''
  if (contact?.pldDeals?.length) {
    if (contact.pldDeals.length > 1) {
      contactSource = `Deal Count: ${contact.pldDeals.length}`
    } else {
      contactSource = `${contact.pldDeals[0].id} `
      contactSourceSecondary = `${contact.pldDeals[0].name}`
    }
  }

  return (
    <ListItem className={classes.listItem} divider button {...(props as any)}>
      <ListItemText
        classes={{ root: classes.listItemText, secondary: classes.secondaryText }}
        primary={primaryText}
        secondary={secondaryText}
        primaryTypographyProps={{
          variant: textBodySize
        }}
        secondaryTypographyProps={{
          variant: textBodySize
        }}
      />

      <div className={classes.details}>
        <Typography variant={textBodySize} classes={{ root: classes.sourceName }} align="right">
          {contactSource}
        </Typography>
        {contactSourceSecondary && (
          <Typography variant={textBodySize} classes={{ root: classes.sourceName }} align="right">
            {contactSourceSecondary}
          </Typography>
        )}
        <Typography variant={textBodySize} classes={{ root: classes.email }} align="right">
          {contact.email}
        </Typography>
        {!!ownerName && (
          <Typography variant={textBodySize} classes={{ root: classes.owner }} align="right">
            {ownerName}
          </Typography>
        )}
      </div>
    </ListItem>
  )
}

export default ContactResultsList
